import { useState, useEffect, useRef } from 'react';
import { Button } from '../primitives/';
import { LuTrash2 } from 'react-icons/lu';
import { useOutletContext } from 'react-router-dom';
import useTimedError from '../../hooks/useTimedError';
import { useDatabase } from '../../contexts/DatabaseContext';
import { useTranslation } from '../../hooks/useTranslation';
import { LIMITS } from '../../Limits';

export default function AutoEngageSettings({ config, workspaceId, workspaceType, setConfig, getRandomWelcomeMessage, autoEngages, setAutoEngages, delay, setDelay, hasChangesAutoEngage, requestedVisualTrigger, requestedVisualIndex }) {
    
    const [err, setErr] = useTimedError();
    const [errors, setErrors] = useState(
        (config?.autoEngages || []).map(() => ({
            message: null,
            path: null
        }))
    );
    const [buttonText, setButtonText] = useState('Update Settings');
    const [loading, setLoading] = useState(false);
    const { updateWorkspaceConfigDoc, createAutoEngageActionEvent } = useDatabase();
    const t = useTranslation();

    const addSetting = () => {
        if (autoEngages.length < 4) {
            setAutoEngages([...autoEngages, { 
                message: getRandomWelcomeMessage(),
                path: '',
            }]);
            setErrors([...errors, { message: null, path: null }]);
        } else {
            setErr(t('You can only have up to 4 auto engages.'));
        }
    };

    function validateTextInput(field, value) {
        if (field === 'message') {
            return value.length <= LIMITS.PROMPT && value.length > 0;
        } else if (field === 'path') {
            return value.length <= LIMITS.PATH;
        }
        return true;
    }
  
    const updateAutoEngage = (index, field, value) => {
        requestedVisualTrigger.current = 'autoEngage';
        requestedVisualIndex.current = index;
        setAutoEngages(prev => {
            const newAutoEngages = [...prev];
            newAutoEngages[index] = { ...newAutoEngages[index], [field]: value };
            return newAutoEngages;
        });
        
        const newErrors = [...errors];
        if (validateTextInput(field, value)) {
            newErrors[index] = { ...newErrors[index], [field]: null };
        } else {
            newErrors[index] = { ...newErrors[index], [field]: t(LIMITS[`${field}Error`]) };
        }
        setErrors(newErrors);
    };
  
    const removeSetting = (index) => {
        setAutoEngages(autoEngages.filter((_, i) => i !== index));
        setErrors(errors.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            createAutoEngageActionEvent(workspaceType, workspaceId, delay, autoEngages.length);
            await updateWorkspaceConfigDoc(workspaceId, { autoEngages, autoEngageDelay: delay });
            setConfig(prev => ({ ...prev, autoEngages, autoEngageDelay: delay }));
        } catch (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "An error occurred while updating the workspace config.",
                    action: "auto_engage",
                }
            }));
        } finally {
            setLoading(false);
            setButtonText('Published!');
            setTimeout(() => {
                setButtonText('Update Settings');
            }, 2000);
        }
    }

    function hasErrors() {
        return errors.some(error => error.message || error.path);
    }

    // React 19 should auto this for useMemo
    const rangeProgress = (delay - 0) / (10 - 0) * 100;
  
    return (
        <div className="settings-container-outer">
            <div className="input-settings-group">
                <div className="input-settings-group-inner">
                    <div>
                        <label className="settings-input-label">Response Timer</label>
                        <p className="settings-input-description">Choose how many seconds before visitor is engaged.</p>
                    </div>
                    <div className="delay-selector">
                        <input
                            type="range"
                            min="0"
                            max="10"
                            value={delay}
                            onChange={(e) => setDelay(parseInt(e.target.value))}
                            style={{ backgroundImage: `linear-gradient(to right, #27ca52 ${rangeProgress}%, #eae9e9 ${rangeProgress}%)` }}
                        />
                        <div className="delay-value">
                            {delay}
                        </div>
                    </div>
                </div>
            </div>

            {autoEngages.map((setting, idx) => (
                <AutoEngageCard 
                    key={idx} 
                    setting={setting} 
                    config={config} 
                    idx={idx} 
                    updateAutoEngage={updateAutoEngage} 
                    removeSetting={removeSetting}
                    error={errors[idx]} 
                />
            ))} 
                
            <Button onClick={addSetting} className="settings" error={err}>
                {err || 'New Engagement'}
            </Button>
            <Button className="submit settings"
                onClick={handleSubmit}
                loading={loading}
                loadingFinish={buttonText === 'Published!'}
                disabled={!hasChangesAutoEngage || hasErrors()}
            >
                {buttonText}
            </Button>
        </div>
    );
}

function AutoEngageCard({ setting, config, idx, updateAutoEngage, removeSetting, error }) {
    const { mobile } = useOutletContext();
    const [showDelete, setShowDelete] = useState(!mobile);
    const cardRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);

    const handleTouchStart = (e) => {
        if (mobile) {
            setStartX(e.touches ? e.touches[0].clientX : e.clientX);
            setIsDragging(false);
        }
    };

    const handleMouseDown = (e) => {
        if (mobile) {
            setStartX(e.clientX);
            setIsDragging(false);
        }
    };

    const handleTouchMove = (e) => {
        if (mobile) {
            const currentX = e.touches ? e.touches[0].clientX : e.clientX;
            const diff = startX - currentX;
            
            // If dragged more than 20px, show delete button
            if (diff > 20 && !isDragging) {
                setIsDragging(true);
                setShowDelete(true);
            }

            if (diff < -20 && !isDragging) {
                setIsDragging(false);
                setShowDelete(false);
            }
        }
    };

    const handleMouseMove = (e) => {
        if (mobile && startX !== 0) {
            const currentX = e.clientX;
            const diff = startX - currentX;
            
            // If dragged more than 20px, show delete button
            if (diff > 20 && !isDragging) {
                setIsDragging(true);
                setShowDelete(true);
            }

            if (diff < -20 && !isDragging) {
                setIsDragging(false);
                setShowDelete(false);
            }
        }
    };

    const handleMouseUp = () => {
        if (mobile) {
            setStartX(0);
        }
    };

    useEffect(() => {
        // Add global mouse up handler
        document.addEventListener('mouseup', handleMouseUp);
        
        // Cleanup
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <div className="input-settings-group-inner-row">
            <div 
                className={`auto-card ${showDelete && mobile ? 'auto-card-with-delete' : ''}`}
                ref={cardRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
            >
                <input
                    type="text"
                    className="auto-card-input"
                    placeholder={'Enter message'}
                    value={setting.message}
                    onChange={(e) => updateAutoEngage(idx, 'message', e.target.value)}
                />
                {error?.message && <p className="auto-card-input-error">{error.message}</p>}
                <input
                    type="text"
                    className="auto-card-input"
                    placeholder={'Set URL path ex: /about'}
                    value={setting.path}
                    onChange={(e) => updateAutoEngage(idx, 'path', e.target.value)}
                />
                {error?.path && <p className="auto-card-input-error bottom">{error.path}</p>}
            </div>
            {(showDelete || !mobile) && (
                <button 
                    className="auto-card-delete-button"
                    onClick={() => removeSetting(idx)}
                >
                    <LuTrash2 />
                </button>
            )}
        </div>
    );
}
